import React, { useContext, useEffect, useState } from 'react';
import batchStructureService from 'ApiService/Admin/batchService';
import courseService from 'ApiService/Admin/courseService';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import { SlArrowLeft } from 'react-icons/sl';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import InputField from 'components/fields/InputField';
import Nodatafound from 'pages/Common/Nodatafound';
import Post from 'components/card/Post';
import { AuthContext } from 'contexts/AuthContext';

const Index = () => {
  const { state } = useContext(AuthContext);
  const role = state?.user?.role;

  const resolvedBranch =
    role === 'superadmin' ? state?.selectedBranch : state?.user?.branch?._id;

  const selectedBranch = resolvedBranch;

  const [batchStructures, setBatchStructures] = useState([]);
  const [departmentCourses, setDepartmentCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [batches, setBatches] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [editData, setEditData] = useState({
    name: '',
    description: '',
    courses: [],
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    id: null,
  });

  /* ================= FETCH BATCH STRUCTURES ================= */
  const fetchBatchStructures = async () => {
    if (role === 'superadmin' && !selectedBranch) {
      setBatchStructures([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await batchStructureService.getAllBatchStructures();
      if (res.data.success) {
        setBatchStructures(res.data.batchStructures || []);
      }
    } catch {
      toast.error('Failed to fetch batch structures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchStructures();
  }, [role, selectedBranch]);

  const selectedStructure = batchStructures.find((bs) => bs._id === selectedId);

  /* ================= FETCH BATCHES ================= */
  const fetchBatches = async (structureId) => {
    if (role === 'superadmin' && !selectedBranch) {
      setBatches([]);
      return;
    }

    try {
      const res = await batchStructureService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );

      if (res.data.success) {
        setBatches(res.data.batches || []);
      }
    } catch {
      toast.error('Failed to fetch batches');
    }
  };

  useEffect(() => {
    if (selectedId) fetchBatches(selectedId);
  }, [selectedId, role, selectedBranch]);

  /* ================= FETCH COURSES BY DEPARTMENT ================= */
  const fetchCoursesByDepartment = async (departmentId) => {
    try {
      const res = await courseService.getCoursesByDepartment(departmentId);
      if (res.data.success) {
        setDepartmentCourses(res.data.courses || []);
      } else {
        setDepartmentCourses([]);
      }
    } catch {
      toast.error('Failed to load courses');
      setDepartmentCourses([]);
    }
  };

  /* ================= EDIT ================= */
  const openEditModal = () => {
    const departmentId =
      typeof selectedStructure.department === 'object'
        ? selectedStructure.department._id
        : selectedStructure.department;

    if (!departmentId) {
      toast.error('Department not found');
      return;
    }

    setEditData({
      name: selectedStructure.name,
      description: selectedStructure.description || '',
      courses: selectedStructure.courses.map((c) => c._id),
    });

    fetchCoursesByDepartment(departmentId);
    setEditModalOpen(true);
  };

  const updateBatchStructure = async () => {
    if (!editData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!editData.courses.length) {
      toast.error('Select at least one course');
      return;
    }

    try {
      const res = await batchStructureService.updateBatchStructure(
        selectedStructure._id,
        editData
      );

      if (res.data.success) {
        toast.success('BatchStructure updated successfully');
        setEditModalOpen(false);
        fetchBatchStructures();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    }
  };

  /* ================= DELETE ================= */
  const performDelete = async () => {
    try {
      const res = await batchStructureService.deleteBatchStructure(
        deleteConfirm.id
      );
      if (res.data.success) {
        toast.success('BatchStructure deleted');
        setSelectedId(null);
        fetchBatchStructures();
      }
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleteConfirm({ isOpen: false, id: null });
    }
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!batchStructures.length) {
    return <Nodatafound title="No Batch Structures Found" />;
  }

  /* ================= DETAIL VIEW ================= */
  if (selectedStructure) {
    return (
      <div className="rounded-[20px] bg-white p-6 shadow-lg">
        <button
          onClick={() => setSelectedId(null)}
          className="mb-6 flex items-center text-brand-500"
        >
          <SlArrowLeft className="mr-2" />
          Back
        </button>

        <div className="mb-4 flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold">{selectedStructure.name}</h2>
            <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
              {selectedStructure.department?.name}
            </span>

            <p>
              <span className="font-bold">Course:</span>{' '}
              {selectedStructure.courses.map((c) => c.courseName).join(', ')}
            </p>

            <p className="font-bold">
              Total Batches:
              <span className="px-2 font-normal">{batches.length}</span>
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={openEditModal}
              className="rounded bg-blue-500 px-4 py-2 text-white"
            >
              Edit
            </button>
            <button
              onClick={() =>
                setDeleteConfirm({
                  isOpen: true,
                  id: selectedStructure._id,
                })
              }
              className="rounded bg-red-500 px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-8">
          <Post
            batches={batches}
            onEditBatchApi={(data) => courseService.editBatch(data)}
            onDeleteBatchApi={(id) => courseService.deleteBatchById(id)}
            onBatchUpdated={() => fetchBatches(selectedId)}
            selectedStructure={selectedStructure}
            selectedBranch={selectedBranch}
          />
        </div>

        {/* ================= EDIT MODAL ================= */}
        {editModalOpen && (
          <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
            <div className="w-full max-w-lg rounded bg-white p-6">
              <h2 className="mb-4 text-xl font-bold">Edit Batch Structure</h2>

              <InputField
                label="Name"
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />

              <InputField
                label="Description"
                value={editData.description}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    description: e.target.value,
                  })
                }
              />

              <div className="mt-4">
                <p className="mb-2 font-semibold">
                  Select Courses ({selectedStructure.department?.name})
                </p>

                <div className="max-h-52 overflow-y-auto rounded border p-3">
                  {departmentCourses.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No courses available
                    </p>
                  ) : (
                    departmentCourses.map((course) => {
                      const checked = editData.courses.includes(course._id);

                      return (
                        <label
                          key={course._id}
                          className="mb-2 flex items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setEditData((prev) => ({
                                ...prev,
                                courses: checked
                                  ? prev.courses.filter(
                                      (id) => id !== course._id
                                    )
                                  : [...prev.courses, course._id],
                              }))
                            }
                          />
                          <span>
                            {course.courseName} ({course.duration} months)
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="rounded border px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={updateBatchStructure}
                  className="rounded bg-brand-500 px-4 py-2 text-white"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
          onConfirm={performDelete}
          title="Confirm Delete"
          message="Delete this BatchStructure?"
        />
      </div>
    );
  }

  /* ================= CARD VIEW ================= */
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {batchStructures.map((bs) => (
        <div
          key={bs._id}
          onClick={() => setSelectedId(bs._id)}
          className="relative cursor-pointer rounded-[20px] border bg-white p-6 shadow"
        >
          <span className="absolute right-4 top-4 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
            {bs.department?.name}
          </span>

          <h3 className="mb-2 text-xl font-bold">{bs.name}</h3>
          <p className="text-sm text-gray-600">Courses: {bs.courses.length}</p>
        </div>
      ))}
    </div>
  );
};

export default Index;
